'use client';

import { useId } from 'react';

import { motion } from 'framer-motion';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export type TabItem<T extends string = string> = {
	label: string;
	value: T;
};

type TabSwitcherProps<T extends string> = {
	tabs: TabItem<T>[];
	activeTab: T;
	onChange: (value: T) => void;
};

export function TabSwitcher<T extends string>({ tabs, activeTab, onChange }: TabSwitcherProps<T>) {
	const indicatorLayoutId = useId();

	return (
		<Tabs
			value={activeTab}
			onValueChange={(v) => onChange(v as T)}
		>
			<TabsList className="m-0 gap-4 bg-transparent p-0">
				{tabs.map((tab) => {
					const isActive = activeTab === tab.value;
					return (
						<TabsTrigger
							key={tab.value}
							value={tab.value}
							className="relative -mb-px rounded-none border-b-2 border-transparent bg-transparent px-2 pb-2 pt-1 text-sm font-normal text-secondary hover:text-primary data-[state=active]:border-transparent data-[state=active]:bg-transparent data-[state=active]:text-primary"
						>
							{tab.label}
							{isActive && (
								<motion.span
									layoutId={`tab-indicator-${indicatorLayoutId}`}
									className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary"
									transition={{
										type: 'tween',
										duration: 0.2,
										ease: 'easeInOut',
									}}
								/>
							)}
						</TabsTrigger>
					);
				})}
			</TabsList>
		</Tabs>
	);
}
